/**
 * Timezone + geolocation helpers for trigger-ai-response payloads.
 *
 * - `local_time` and `timezone` are computed fresh on every trigger.
 * - Geo fields (`country`, `country_region`, `ip_city`) are fetched once
 *   per session from `/api/geo` (Vercel IP headers).
 */

// ---------------------------------------------------------------------------
// Geo cache (fetched once per session)
// ---------------------------------------------------------------------------

interface GeoCache {
  country: string | null;
  country_region: string | null;
  ip_city: string | null;
}

let geoCache: GeoCache = { country: null, country_region: null, ip_city: null };
let fetchAttempted = false;

/**
 * Fetch IP-based geo data from the Vercel serverless function.
 * Called once on app boot; result is cached for the session.
 */
export const fetchGeoData = async (): Promise<void> => {
  if (fetchAttempted) return;
  fetchAttempted = true;

  try {
    const res = await fetch("/api/geo");
    if (res.ok) {
      const data = await res.json();
      geoCache = {
        country: data.country || null,
        country_region: data.country_region || null,
        ip_city: data.ip_city || null,
      };
    }
  } catch {
    // Silently fail — geo fields are optional.
  }
};

// ---------------------------------------------------------------------------
// local_time — ISO 8601 with the user's UTC offset (NOT toISOString / Z)
// ---------------------------------------------------------------------------

export const localTimeIso = (): string => {
  const d = new Date();
  const off = -d.getTimezoneOffset(); // minutes east of UTC
  const sign = off >= 0 ? "+" : "-";
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(off / 60)}:${pad(off % 60)}`
  );
};

// ---------------------------------------------------------------------------
// timezone — IANA name from the browser
// ---------------------------------------------------------------------------

export const timeZoneName = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Assembled payload fields for trigger-ai-response
// ---------------------------------------------------------------------------

export interface TriggerGeoFields {
  local_time: string;
  timezone?: string | null;
  country?: string | null;
  country_region?: string | null;
  ip_city?: string | null;
}

/**
 * Returns the 5 optional context fields to merge into every
 * `trigger-ai-response` payload. Unknown values are null (or omitted).
 * `local_time` and `timezone` are computed fresh; geo fields use the cache.
 */
export const getTriggerGeoFields = (): TriggerGeoFields => {
  const fields: TriggerGeoFields = {
    local_time: localTimeIso(),
  };

  const tz = timeZoneName();
  if (tz) fields.timezone = tz;

  if (geoCache.country) fields.country = geoCache.country;
  if (geoCache.country_region) fields.country_region = geoCache.country_region;
  if (geoCache.ip_city) fields.ip_city = geoCache.ip_city;

  return fields;
};
